/**
 * Campaign health — the diagnostic layer over the raw analytics rollups.
 *
 * The campaign builds PIPELINE, but the story is *session delivery*, not a single opportunity's stage
 * progress. A campaign attributes one Salesforce opportunity today (its Amount, via `useCampaignPipeline`
 * → `campaignLeader.deliveryPipeline`), yet a workshop drives many opportunities over its life. So the
 * dollars are an ATTRIBUTION anchor — how much pipeline this motion is credited with — while the leading
 * indicators of *future* pipeline are the quantity of delivery sessions run and how far each progresses.
 * This module turns the raw rows into: (1) session volume + progression, the headline, and (2) a small
 * set of plain-language signals answering *are sessions delivering, and why or why not* — with the
 * attributed dollars folded in as context, never as the sole number.
 *
 * Four signals, chosen with the user:
 *   - `stalled`         — an in-progress delivery with no step activity for {@link STALLED_DAYS} days;
 *                         its linked pipeline $ is stuck. (risk)
 *   - `behind_schedule` — an open delivery whose scheduled date has passed. (attention)
 *   - `readiness_gap`   — SEs who haven't cleared the enablement gate; risk when one owns an active
 *                         delivery (unready owner ⇒ that pipeline is exposed). (attention/risk)
 *   - `low_assessment`  — cohort enablement progress is lagging on average. (attention)
 *
 * Pure module: no React, no fetching, `now` is injectable so it is deterministically testable. Callers
 * pass already-fetched rows and the per-delivery pipeline $ they computed from `campaignLeader`.
 */
import {
  CampaignDelivery,
  CampaignDeliveryStepStatus,
  CampaignEnrollment,
} from './campaigns';
import { enrollmentPercent, fmtDate } from './campaignAnalytics';
import { formatPipelineUsd } from './campaignLeader';

/** A delivery with no recorded step activity for this many days is treated as stalled. */
export const STALLED_DAYS = 14;
/** Cohort average enablement progress below this percent flags a `low_assessment` signal. */
export const LOW_ASSESSMENT_PCT = 50;

const DAY_MS = 86_400_000;

export type HealthLevel = 'ok' | 'attention' | 'risk';

export type HealthSignalKind = 'stalled' | 'behind_schedule' | 'readiness_gap' | 'low_assessment';

export interface HealthSignal {
  kind: HealthSignalKind;
  /** Only degraded levels produce a signal; `ok` never does. */
  level: 'attention' | 'risk';
  /** A finished human sentence, e.g. `Acme — $250K stalled, no activity 21d, at Deep-dive`. */
  message: string;
  /** Pipeline $ this signal exposes (0 when the signal isn't dollar-attributable). */
  amount: number;
}

export interface HealthResult {
  /** Worst level across the signals; `ok` when there are none. */
  level: HealthLevel;
  signals: HealthSignal[];
}

const LEVEL_RANK: Record<HealthLevel, number> = { ok: 0, attention: 1, risk: 2 };

/** The worst (highest-severity) level in a list; `ok` for an empty list. */
export function worstLevel(levels: HealthLevel[]): HealthLevel {
  return levels.reduce<HealthLevel>((acc, l) => (LEVEL_RANK[l] > LEVEL_RANK[acc] ? l : acc), 'ok');
}

/** Whole days between an ISO timestamp and `now`; null when missing/unparseable. */
function daysSince(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((now - t) / DAY_MS);
}

/** Days since the most recent activity across a delivery's step statuses; null when none recorded. */
function daysSinceLastActivity(statuses: CampaignDeliveryStepStatus[], now: number): number | null {
  let best: number | null = null;
  for (const s of statuses) {
    for (const iso of [s.last_updated, s.completed_at, s.created]) {
      const d = daysSince(iso, now);
      if (d != null && (best == null || d < best)) best = d;
    }
  }
  return best;
}

/** The name of the step a delivery is currently working (first in-progress), if any. */
function currentStepName(statuses: CampaignDeliveryStepStatus[]): string | null {
  const active = statuses.find((s) => s.status === 'in_progress');
  return active?.step_name ?? null;
}

const deliveryLabel = (d: CampaignDelivery): string => d.name ?? `Delivery #${d.id}`;
const isOpen = (d: CampaignDelivery): boolean => d.status !== 'completed' && d.status !== 'cancelled';

/**
 * Health of a single delivery. `amount` is the pipeline $ linked to it (via `deliveryPipeline`), folded
 * into each signal's sentence so a stalled/overdue delivery reads as stalled/overdue *dollars*.
 *
 * `opts.checkStalled` (default true) gates the stalled signal, which needs the delivery's step-status
 * rows. A caller that has NOT loaded them (e.g. the program-level table) must pass `false`; otherwise an
 * empty `stepStatuses` is indistinguishable from "no activity" and every in-progress delivery would be
 * mislabelled stalled.
 */
export function deliveryHealth(
  delivery: CampaignDelivery,
  stepStatuses: CampaignDeliveryStepStatus[],
  amount = 0,
  now: number = Date.now(),
  opts: { checkStalled?: boolean } = {},
): HealthResult {
  const { checkStalled = true } = opts;
  const signals: HealthSignal[] = [];
  const label = deliveryLabel(delivery);
  const usd = amount > 0 ? `${formatPipelineUsd(amount)} ` : '';

  // Behind schedule: a scheduled date in the past on an open (non-draft) delivery.
  if (isOpen(delivery) && delivery.status !== 'draft') {
    const overdue = daysSince(delivery.scheduled_for, now);
    if (overdue != null && overdue > 0) {
      signals.push({
        kind: 'behind_schedule',
        level: 'attention',
        amount,
        message: `${label} — ${usd}past due (scheduled ${fmtDate(delivery.scheduled_for)}, ${overdue}d ago)`,
      });
    }
  }

  // Stalled: an in-progress delivery with no recent step activity — stuck pipeline.
  if (checkStalled && delivery.status === 'in_progress') {
    const idle = daysSinceLastActivity(stepStatuses, now);
    if (idle == null || idle >= STALLED_DAYS) {
      const stuck = currentStepName(stepStatuses);
      const idleText = idle == null ? 'no recorded activity' : `no activity ${idle}d`;
      signals.push({
        kind: 'stalled',
        level: 'risk',
        amount,
        message: `${label} — ${usd}stalled, ${idleText}${stuck ? `, at ${stuck}` : ''}`,
      });
    }
  }

  return { level: worstLevel(signals.map((s) => s.level)), signals };
}

/**
 * Cohort readiness from enrollments. An SE is "ready" once their enablement enrollment is completed.
 * A gap is a `risk` (not just attention) when an unready SE owns an active delivery — that delivery's
 * pipeline is being run by someone who hasn't finished ramping.
 */
export function readinessHealth(
  enrollments: CampaignEnrollment[],
  deliveries: CampaignDelivery[],
  now: number = Date.now(),
): HealthResult {
  const signals: HealthSignal[] = [];
  const total = enrollments.length;
  if (total === 0) return { level: 'ok', signals };

  const notReady = enrollments.filter((e) => e.status !== 'completed');

  if (notReady.length > 0) {
    const activeOwners = new Set(
      deliveries
        .filter((d) => d.status === 'in_progress' || d.status === 'scheduled')
        .map((d) => (d.owner ?? '').trim().toLowerCase())
        .filter(Boolean),
    );
    const exposedOwner = notReady.find(
      (e) => e.user_email && activeOwners.has(e.user_email.trim().toLowerCase()),
    );
    signals.push({
      kind: 'readiness_gap',
      level: exposedOwner ? 'risk' : 'attention',
      amount: 0,
      message: exposedOwner
        ? `${notReady.length} of ${total} SEs not workshop-ready — ${exposedOwner.user_email} owns an active delivery`
        : `${notReady.length} of ${total} SEs not workshop-ready`,
    });
  }

  const avg = Math.round(enrollments.reduce((sum, e) => sum + enrollmentPercent(e), 0) / total);
  if (avg < LOW_ASSESSMENT_PCT) {
    signals.push({
      kind: 'low_assessment',
      level: 'attention',
      amount: 0,
      message: `Enablement lagging — cohort ${avg}% complete on average`,
    });
  }

  // `now` is accepted for signature symmetry with the other health fns / future date-based rules.
  void now;
  return { level: worstLevel(signals.map((s) => s.level)), signals };
}

export interface CampaignHealthSummary extends HealthResult {
  /** Compact diagnostic line, e.g. `1 delivery stalled · 3 SEs not ready`; `On track` when clean. */
  headline: string;
  /** Total delivery sessions the campaign has run/planned — the primary volume metric. */
  deliveryCount: number;
  /** Sessions delivered end-to-end. */
  deliveriesCompleted: number;
  /** Sessions live now (in progress or scheduled) — the in-flight quantity. */
  deliveriesActive: number;
  /** Attributed pipeline $ across the campaign's deliveries (context, not the headline). */
  pipelineTotal: number;
  /** Attributed $ sitting on sessions that aren't progressing (stalled/behind) — attribution stuck, not lost. */
  pipelineStuck: number;
}

/**
 * Roll every delivery's health plus cohort readiness into one campaign summary: session volume +
 * progression (the headline), the diagnostic signals, and the attributed pipeline $ as context.
 * `amountByDelivery` is the per-delivery pipeline $ the caller computed from
 * `campaignLeader.deliveryPipeline` (kept out of this module so health stays decoupled from the
 * Salesforce fetch shape). `stepStatuses` is the flat rollup array; it is grouped by delivery here.
 */
export function campaignHealthSummary(args: {
  deliveries: CampaignDelivery[];
  enrollments: CampaignEnrollment[];
  stepStatuses: CampaignDeliveryStepStatus[];
  amountByDelivery?: Map<number, number>;
  /** Pass false where step statuses were not loaded (program level) — see {@link deliveryHealth}. */
  checkStalled?: boolean;
  now?: number;
}): CampaignHealthSummary {
  const {
    deliveries,
    enrollments,
    stepStatuses,
    amountByDelivery,
    checkStalled = true,
    now = Date.now(),
  } = args;

  const statusesByDelivery = new Map<number, CampaignDeliveryStepStatus[]>();
  for (const s of stepStatuses) {
    const list = statusesByDelivery.get(s.delivery) ?? [];
    list.push(s);
    statusesByDelivery.set(s.delivery, list);
  }

  let pipelineTotal = 0;
  let pipelineStuck = 0;
  let deliveriesCompleted = 0;
  let deliveriesActive = 0;
  let stalledCount = 0;
  let behindCount = 0;
  const signals: HealthSignal[] = [];

  for (const d of deliveries) {
    if (d.status === 'completed') deliveriesCompleted += 1;
    if (d.status === 'in_progress' || d.status === 'scheduled') deliveriesActive += 1;

    const amount = amountByDelivery?.get(d.id) ?? 0;
    pipelineTotal += amount;
    const health = deliveryHealth(d, statusesByDelivery.get(d.id) ?? [], amount, now, { checkStalled });
    if (health.signals.length > 0) pipelineStuck += amount;
    for (const sig of health.signals) {
      signals.push(sig);
      if (sig.kind === 'stalled') stalledCount += 1;
      if (sig.kind === 'behind_schedule') behindCount += 1;
    }
  }

  const readiness = readinessHealth(enrollments, deliveries, now);
  signals.push(...readiness.signals);

  const notReady = enrollments.filter((e) => e.status !== 'completed').length;
  const parts: string[] = [];
  if (stalledCount > 0) parts.push(`${stalledCount} deliver${stalledCount > 1 ? 'ies' : 'y'} stalled`);
  if (behindCount > 0) parts.push(`${behindCount} behind schedule`);
  if (notReady > 0) parts.push(`${notReady} SE${notReady > 1 ? 's' : ''} not ready`);
  if (readiness.signals.some((s) => s.kind === 'low_assessment') && notReady === 0) {
    parts.push('enablement lagging');
  }

  return {
    level: worstLevel(signals.map((s) => s.level)),
    signals,
    headline: parts.length ? parts.join(' · ') : 'On track',
    deliveryCount: deliveries.length,
    deliveriesCompleted,
    deliveriesActive,
    pipelineTotal,
    pipelineStuck,
  };
}
